import type { CheckoutCloseReason, CheckoutPaymentStatus, GenericCheckoutPosition } from '@/domain/checkout';
import type { Platform } from '@/domain/deviceConfig';
import type { InitiatorType, MessageSubType } from '@/domain/messageEnums';
import { INITIATOR_TYPES } from '@/domain/messageEnums';

export interface Analytics {
  /**
   * Track when the express checkout box closes without a purchase.
   * @param params.reason - What closed it; a generic-checkout navigation is not reported here
   */
  trackCheckoutCancelled(params: {
    amount: string;
    currency: string;
    initiator: Initiator;
    methodCount: number;
    reason: CheckoutCloseReason;
    subtotal?: string;
  }): void;

  /** Track when the express checkout box opens, having been closed. */
  trackCheckoutInitiated(params: {
    degraded?: string[];
    amount: string;
    currency: string;
    genericOffered: boolean;
    initiator: Initiator;
    methodCount: number;
    methodIds: string[];
    genericPosition?: GenericCheckoutPosition;
    subtotal?: string;
  }): void;

  /**
   * Track when a consumer taps a checkout option.
   * @param params.position - Index of the option in the rendered stack
   */
  trackCheckoutOptionTapped(params: {
    amount: string;
    currency: string;
    initiator: Initiator;
    kind: CheckoutOptionKind;
    methodCount: number;
    methodId: string;
    position: number;
    subtotal?: string;
  }): void;

  /**
   * Track when the user types the first character of a new conversation.
   * @param params.initiator - Who typed the message
   */
  trackFirstMessageTyped(params: { initiator: Initiator }): void;

  /**
   * Emit the GA `conversation:started` signal. Normally fired inside
   * trackMessageReceived for an initial message, but a machine-generated first
   * message (a checkout completion) suppresses that call and emits this directly
   * so the signal is not lost.
   */
  trackConversationStarted(): void;

  /**
   * Track when system receives a message from the end user.
   * @param params.campaignId - ID of the campaign that initiated the conversation, if any
   * @param params.initiator - Who sent the message
   * @param params.isInitialMessage - Whether this is the first message from the user in the conversation
   * @param params.messageId - Unique identifier for the message
   * @param params.messageLength - Character length of the message content
   * @param params.subType - The message subtype (e.g., TEXT, IMAGE, FILE)
   */

  trackMessageReceived(params: {
    campaignId?: string;
    initiator: Initiator;
    isInitialMessage: boolean;
    messageId: string;
    messageLength: number;
    subType?: MessageSubType;
  }): void;

  /**
   * Track when system sends a message to the end user.
   * @param params.initiator - Who sent the message (agent, bot, API, etc.)
   * @param params.messageId - Unique identifier for the message
   * @param params.messageLength - Character length of the message content
   * @param params.subType - The message subtype (e.g., TEXT, IMAGE, FILE)
   */
  trackMessageSent(params: {
    initiator: Initiator;
    messageId: string;
    messageLength: number;
    subType?: MessageSubType;
  }): void;

  /**
   * Track when a consumer clicks on a recommended product card.
   * @param params.initiator - Who clicked the product
   * @param params.product - Lightweight summary of the clicked product
   */
  trackProductClicked(params: { initiator: Initiator; product: RecommendedProduct }): void;

  /**
   * Track when a message containing product recommendations is sent.
   * @param params.initiator - Who sent the message
   * @param params.messageId - Unique identifier for the message
   * @param params.products - Lightweight summary of each recommended product
   */
  trackProductsRecommended(params: { initiator: Initiator; messageId: string; products: RecommendedProduct[] }): void;

  /**
   * Track when a consumer authorizes a payment through express checkout.
   * @param params.status - `authorized` (a wallet authorized, not captured),
   * `completed` (an order was placed), or `failed` (a completion ran but placed
   * no order)
   */
  trackPurchaseCompleted(params: {
    amount: string;
    currency: string;
    initiator: Initiator;
    methodId: string;
    orderRef?: string;
    status: CheckoutPaymentStatus;
    subtotal?: string;
  }): void;

  /**
   * Track when a conversation/session ends.
   * @param params.reason - The reason the session ended (e.g., AGENT_ENDED, TIME_EXPIRED)
   */
  trackSessionEnded(params: { reason: string }): void;

  /**
   * Track when the widget is loaded.
   */
  trackWidgetLoaded(): void;

  /**
   * Track when the widget view changes (e.g., maximized to sidebar).
   * @param params.fromState - The widget state before the change
   * @param params.initiator - Who initiated the change
   * @param params.toState - The widget state after the change
   */
  trackWidgetViewChanged(params: {
    fromState: WidgetState;
    initiator: Initiator;
    source?: WidgetTransitionSource;
    toState: WidgetState;
  }): void;
}

export type DeviceType = 'mobile' | 'desktop';

export interface WidgetContext {
  appId: string;
  deviceType: DeviceType;
  legacyUI: boolean;
  pageUrl: string;
  platform: Platform;
  widgetState: WidgetState;
}

export interface VisitorContext {
  clientId: string;
  gladlyCustomerId?: string;
}

/**
 * A widget context after the `pageUrl` filter. Carries `pageUrl` only for the event
 * types in {@link PAGE_URL_EVENTS}; {@link WidgetContext} always carries one.
 */
export type FilteredWidgetContext = Omit<WidgetContext, 'pageUrl'> & { pageUrl?: string };

export interface BaseAnalyticsEvent extends WidgetContext, VisitorContext {
  initiator: Initiator;
}

export const ACTION_TYPES = {
  CART_QUANTITY_UPDATED: 'chat widget cart quantity updated',
  CHECKOUT_CANCELLED: 'chat widget checkout cancelled',
  CHECKOUT_INITIATED: 'chat widget checkout initiated',
  CHECKOUT_OPTION_TAPPED: 'chat widget checkout option tapped',
  FIRST_MESSAGE_TYPED: 'chat widget first message typed',
  MESSAGE_RECEIVED: 'chat widget message received',
  MESSAGE_SENT: 'chat widget message sent',
  NAVIGATED_TO_URL: 'chat widget navigated to url',
  ORDER_CAPTURED: 'chat widget order captured',
  PRODUCT_ADDED_TO_CART: 'chat widget product added to cart',
  PRODUCT_CLICKED: 'chat widget product clicked',
  PRODUCT_REMOVED_FROM_CART: 'chat widget product removed from cart',
  PRODUCTS_RECOMMENDED: 'chat widget products recommended',
  PRODUCTS_SEARCHED: 'chat widget products searched',
  // "completed" is the spec name; the event's `status` says what actually
  // happened — `authorized` (approved, not captured), `completed` (an order was
  // placed), or `failed`. Do not read revenue off the name alone.
  PURCHASE_COMPLETED: 'chat widget purchase completed',
  TOOL_CALLED: 'chat widget tool called',
  WIDGET_INPUT_FOCUSED: 'chat widget input focused',
  WIDGET_LOADED: 'chat widget loaded',
  WIDGET_VIEW_CHANGED: 'chat widget view changed',
} as const;

export type AnalyticsEventActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];

/** The only event types whose envelope carries `pageUrl`, which is Commerce Info PII. */
export const PAGE_URL_EVENTS: ReadonlySet<AnalyticsEventActionType> = new Set([
  ACTION_TYPES.MESSAGE_RECEIVED,
  ACTION_TYPES.PRODUCT_ADDED_TO_CART,
  ACTION_TYPES.PRODUCT_CLICKED,
  ACTION_TYPES.PRODUCTS_RECOMMENDED,
  ACTION_TYPES.WIDGET_LOADED,
  ACTION_TYPES.WIDGET_VIEW_CHANGED,
]);

/**
 * The money fields shared across checkout and captured-order events. `amount`
 * is the exact decimal the box displayed, as a string in major units; decimals
 * appear as displayed and are optional (`"19.99"`, `"1999"` = 1999 major
 * units), with the currency in `currency`. A string — unlike the cart events'
 * float fields — so the displayed figure reaches the warehouse without float
 * drift.
 */
export interface CheckoutEventMoney {
  amount: string;
  currency: string;
}

// There is deliberately no `checkout unavailable` event: the refusal paths
// return before the box exists, so there is no consumer-visible moment to report.

export interface CheckoutCancelledEvent extends BaseAnalyticsEvent, CheckoutEventMoney {
  action: typeof ACTION_TYPES.CHECKOUT_CANCELLED;
  methodCount: number;
  // No `tappedBefore` field: whether the consumer tapped an option before
  // abandoning is derivable by joining the earlier option-tapped event on the
  // same `clientId`.
  reason: CheckoutCloseReason;
  subtotal?: string;
}

export interface CheckoutInitiatedEvent extends BaseAnalyticsEvent, CheckoutEventMoney {
  action: typeof ACTION_TYPES.CHECKOUT_INITIATED;
  /** Providers that could not answer availability (`<providerId>:<reason>`),
   * present only when at least one degraded — a dead wallet in a shipped
   * config is a silent conversion killer with no other production signal. */
  degraded?: string[];
  genericOffered: boolean;
  genericPosition?: GenericCheckoutPosition;
  methodCount: number;
  methodIds: string[];
  subtotal?: string;
}

export interface CheckoutOptionTappedEvent extends BaseAnalyticsEvent, CheckoutEventMoney {
  action: typeof ACTION_TYPES.CHECKOUT_OPTION_TAPPED;
  kind: CheckoutOptionKind;
  methodCount: number;
  methodId: string;
  position: number;
  subtotal?: string;
}

export type CheckoutOptionKind = 'generic' | 'wallet';

export interface FirstMessageTypedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.FIRST_MESSAGE_TYPED;
}

export interface MessageReceivedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.MESSAGE_RECEIVED;
  campaignId?: string;
  isInitialMessage: boolean;
  messageId: string;
  messageLength: number;
  subType?: MessageSubType;
}

export interface MessageSentEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.MESSAGE_SENT;
  messageId: string;
  messageLength: number;
  subType?: MessageSubType;
}

export interface RecommendedProduct {
  name: string;
  pageUrl: string;
  primaryImageUrl: string;
}

export interface ProductClickedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.PRODUCT_CLICKED;
  product: RecommendedProduct;
}

export interface ProductsRecommendedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.PRODUCTS_RECOMMENDED;
  messageId: string;
  products: RecommendedProduct[];
}

export interface PurchaseCompletedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.PURCHASE_COMPLETED;
  amount: string;
  currency: string;
  methodId: string;
  orderRef?: string;
  status: CheckoutPaymentStatus;
  subtotal?: string;
}

export interface WidgetInputFocusedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.WIDGET_INPUT_FOCUSED;
}

export interface WidgetLoadedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.WIDGET_LOADED;
}

export interface WidgetViewChangedEvent extends BaseAnalyticsEvent {
  action: typeof ACTION_TYPES.WIDGET_VIEW_CHANGED;
  fromState: WidgetState;
  toState: WidgetState;
}

export type CartQuantityUpdatedValue = {
  cartLineRef: string;
  cartSubtotal?: number;
  currency?: string;
  integration: string;
  lineTotal?: number;
  newQuantity: number;
  productName?: string;
  productRef?: string;
};

export type ProductAddedToCartValue = {
  cartSubtotal?: number;
  currency?: string;
  integration: string;
  lineTotal?: number;
  productName?: string;
  productRef: string;
  quantity: number;
  unitPrice?: number;
};

export type ProductRemovedFromCartValue = {
  cartLineRef?: string;
  cartSubtotal?: number;
  currency?: string;
  integration: string;
  productName?: string;
  productRef?: string;
  quantity?: number;
};

export type ProductsSearchedValue = {
  integration: string;
  resultCount: number;
};

export type NavigatedToUrlValue = {
  navigatedFrom: string;
  navigatedTo: string;
};

/**
 * A line item on a captured order. Product-identifier fields are optional
 * because different vendors emit different subsets (Shopify uses variant
 * GIDs, SFCC uses SKUs, Magento distinguishes `variantId` from `sku`). No
 * `productName` and no vendor-attribute pass-through: the Foundation
 * PII-free spec treats product names as PII, and an unfiltered attributes
 * bag would carry consumer-email / shipping-address fields off the
 * confirmation page.
 */
export interface NormalizedLineItem {
  pageUrl?: string;
  productId?: string;
  /** Absent when vendor payload didn't expose a quantity. Never defaulted to 1 — a manufactured 1 reads identically to a real one downstream, forever. */
  quantity?: number;
  sku?: string;
  /** Absent when vendor payload didn't expose a per-line total. Never derived from `unitPrice * quantity` — Tealium's `product_price[]` gives the unit price only; SFCC/Adobe may give the total only. Downstream must handle either shape. */
  totalPrice?: CheckoutEventMoney;
  /** Absent when vendor payload didn't expose a per-unit price. Never derived from `totalPrice / quantity`. Downstream must handle either shape. */
  unitPrice?: CheckoutEventMoney;
  variantId?: string;
}

/**
 * Which attribution handler produced the order: the manifest entry's `name`,
 * i.e. the `ATTRIBUTION_HANDLERS` registry key. Used for dedup ranking when
 * multiple handlers read the same order confirmation.
 */
export type OrderCaptureHandler =
  | 'tealium'
  | 'adobe-ceddl'
  | 'ga4-datalayer'
  | 'shopify-web-pixel'
  | 'hydration-fallback';

/**
 * Which widget code path produced the capture. `wrap` is the interception
 * handler (a monkey-patch on the vendor's dispatch method); `observe` is the
 * page-state observer (reads `utag_data` / `dataLayer` on native page
 * signals and touches no vendor code). Stamped by the runtime, never by an
 * adapter, so the two paths compare row-for-row in the warehouse.
 */
export type OrderCaptureMethod = 'wrap' | 'observe';

/**
 * Captured order as it lands on the widget analytics wire. Payload for
 * `ORDER_CAPTURED`. The payload itself carries no identity; the envelope's
 * `clientId` — plus `gladlyCustomerId` when the visitor is authenticated —
 * is the only identity signal on the event. Vendor-supplied identity
 * resolution (email/phone hashes, platform customer ids) is out of scope
 * for v1 and even hashed identifiers count as PII under the Foundation spec.
 *
 * Two categories of field, cleanly separated:
 *
 * - **Widget-generated** (`capturedAt`, `captureMethod`, `handler`, `sourceVersion`, `totalField`): always
 *   provided by the widget/adapter itself — timestamps and identity of the
 *   capture code path, not vendor data.
 * - **Vendor-data** (`orderId`, `eventId`, money fields, `items`,
 *   `orderedAt`): read directly from the vendor payload. The adapter MUST
 *   NOT derive absent vendor-data fields — money-field semantics (tax
 *   jurisdiction, discount timing, whether shipping is taxed, whether
 *   `total` includes tax or shipping) are vendor-specific, and deriving
 *   them from other fields fabricates false precision on values the
 *   downstream consumer will treat as ground truth.
 *
 * Optional vendor-data fields are absent when the vendor payload didn't
 * expose them. Downstream reporting must distinguish absent (unknown) from
 * `{amount: '0', ...}` (actually zero) — defaulting to zero would silently
 * double-count "unknown" as "no charge" in aggregate queries. `total` is
 * always required — every completed order carries one.
 */
export type NormalizedOrder = {
  /** ISO 8601 — when the widget observed the vendor event. Widget-generated. */
  capturedAt: string;
  /** Which widget capture path produced this row. Widget-generated; stamped by the runtime at publish. */
  captureMethod: OrderCaptureMethod;
  /**
   * Absent when vendor payload didn't expose a discount field. Never derived — not the same as `{amount: '0', ...}`.
   * The vendor's discount amount as sent, sign included. Positive means an amount off under the Tealium Retail,
   * W3C CEDDL and GA4 standards; any other value is for downstream to interpret, not for an adapter to correct.
   */
  discount?: CheckoutEventMoney;
  /** Vendor's coupon / promo code string, when the payload carries one. Distinct from `discount` (an amount) — some vendors (e.g. Balsam Tealium's `order_coupon_code`) expose the code but no amount, and downstream reporting may want to correlate captures with specific promo campaigns. */
  discountCode?: string;
  /** Vendor's event/transaction identifier — the sink's dedup key. Typically equals `orderId` for Tealium, but adapters are free to supply a distinct value; keeping the two separate lets us cross-check the vendor's dedup identifier against the order it claims to be. */
  eventId: string;
  /**
   * Empty array is valid and means no per-item detail, which covers both a
   * vendor that exposed no item data and an order that genuinely has none.
   * The two are not distinguished: unlike a money field, where a defaulted
   * zero drags down every average it lands in, an empty array contributes
   * nothing to an aggregate either way, so one shape is worth more to
   * consumers than the distinction is.
   */
  items: NormalizedLineItem[];
  /**
   * ISO 8601 — when this browser's shopper last sent a chat message, read from
   * the engagement marker at publish. A quick filter for reporting windows; the
   * join on session events stays the authority. Omitted when the capture path
   * keeps no marker.
   */
  lastEngagedAt?: string;
  /** ISO 8601 — vendor's placed-at, when the payload carries one. Omitted rather than defaulted so downstream reporting can decide fallback policy explicitly. */
  orderedAt?: string;
  /** Vendor's order identifier — the number a customer sees on their receipt. Ground truth for downstream cross-reference. */
  orderId: string;
  /**
   * Present and `true` when the vendor emitted `orderId` as a JSON number outside
   * JavaScript's safe-integer range, so the value was already rounded by the page's
   * own parser before any Gladly code saw it.
   *
   * The order is still published: the customer's own tags read the same rounded
   * value, so it matches their analytics and is a stable key against anything else
   * that went through a browser. What it will not match is a source that kept full
   * precision — a server-side merchant webhook, say — so a reconciler joining the
   * two needs to know the browser's copy is approximate rather than silently
   * treating a mismatch as a different order.
   */
  orderIdApproximate?: boolean;
  /**
   * Widget-generated. Present and `true` when this order was already on the page
   * at the moment capture started, and capture started because the shopper sent
   * a chat message rather than because they had chatted before.
   *
   * It marks the sale chat *followed* rather than preceded. Consumers are
   * expected to exclude these from attributed revenue; the row is published
   * anyway because a flagged order can be recovered and a withheld one cannot.
   * A shopper whose engagement marker was evicted -- Safari clears script
   * storage after seven days -- is the case that matters: suppressing would drop
   * a genuinely attributable sale for good, while a flag lets a consumer rejoin
   * it through `gladlyCustomerId`.
   *
   * Page state rather than a clock, deliberately. `capturedAt` is when we read
   * the order, `orderedAt` is absent on every vendor payload recorded so far,
   * and a dataLayer timestamp is rewritten on every page load -- so a
   * confirmation page reopened a day later presents exactly the same times as
   * the original. Whether the order was *already there* is observable where
   * whether it was *already placed* is not.
   *
   * **It does not catch every case, and must not be read as if it does.** The
   * engagement marker is written by any shopper message, including one sent
   * after buying, so a later page load reads that marker as "chatted before"
   * and publishes the same order again unflagged. Collapsing an order once any
   * copy carries this flag is what closes that, and it belongs downstream.
   *
   * Omitted rather than `false`, so absent keeps meaning "this row predates the
   * field" rather than "chat came first".
   */
  precededEngagement?: boolean;
  /** Absent when vendor payload didn't expose a shipping field. Never derived — not the same as `{amount: '0', ...}`. */
  shipping?: CheckoutEventMoney;
  /**
   * Which attribution handler emitted this capture: the manifest entry's `name`. Widget-generated.
   * Named `handler` rather than `source` because the analytics envelope already carries `source: 'CHAT_WIDGET'`
   * at the top level, and one name with two meanings at two depths of the same document is a query hazard.
   */
  handler: OrderCaptureHandler;
  /** Vendor schema version the handler was written against. Widget-generated. */
  sourceVersion: string;
  /** Absent when vendor payload didn't expose a subtotal field. Never derived — not the same as `{amount: '0', ...}`. */
  subtotal?: CheckoutEventMoney;
  /** Absent when vendor payload didn't expose a tax field. Never derived — not the same as `{amount: '0', ...}`. */
  tax?: CheckoutEventMoney;
  total: CheckoutEventMoney;
  /**
   * Which field of the vendor payload `total` was read from, verbatim
   * (`transactionTotal`, `priceWithTax`, `basePrice`, `order_total`, …).
   * Widget-generated.
   *
   * Some vendors expose one grand total and some expose a ranked set that
   * differ in what they include. Adobe CEDDL is the latter: `transactionTotal`
   * is the grand total, `priceWithTax` excludes shipping, and `basePrice`
   * excludes tax and shipping both. An adapter that falls down that chain
   * publishes a smaller number for the same order, and without this field the
   * two rows are indistinguishable in the warehouse.
   *
   * Recorded for every capture, not only the fallbacks, so the column is
   * uniformly populated and absent means "row predates this field" rather
   * than "this adapter declined to say".
   */
  totalField?: string;
  /**
   * The vendor's own event name, verbatim, as it appeared on the payload the
   * capture was read from (`purchase`, `purchase_gtm`, `purchase_refund`, …).
   * Vendor-data, never normalized. Absent when the capture was matched on
   * `page_type` alone and the payload carried no event name.
   *
   * Carried so the widget does not have to be the only place that decides what
   * counts as a completed sale. The widget applies a deliberately narrow rule
   * (see `isPurchaseEvent`); recording what it actually saw lets downstream
   * widen or narrow that verdict as a query, and makes an unrecognized vendor
   * event name visible in the data instead of silently dropped.
   */
  vendorEvent?: string;
  /** The vendor's `page_type` verbatim, when the payload carried one. Vendor-data. Same rationale as `vendorEvent`. */
  vendorPageType?: string;
};

/**
 * What an adapter or handler hands the runtime: a `NormalizedOrder` minus the
 * runtime-stamped `captureMethod`. Keeps vendor marshalling ignorant of which
 * widget path invoked it; the runtime completes the row at publish.
 */
export type CapturedOrderPayload = Omit<NormalizedOrder, 'captureMethod'>;

/**
 * Coverage event emitted for every Beacon tool call, independent of the typed
 * commerce events. `ok` means the handler resolved — a tool that reports a
 * domain-level failure in its own result shape still counts as ok.
 *
 * A tool with a typed mapper emits BOTH this event and its typed event, so the
 * two families overlap: a touchpoint count that unions them double-counts
 * mapped tools.
 */
export type ToolCalledValue = {
  integration: string;
  ok: boolean;
  toolName: string;
};

export interface CartQuantityUpdatedEvent extends BaseAnalyticsEvent, CartQuantityUpdatedValue {
  action: typeof ACTION_TYPES.CART_QUANTITY_UPDATED;
}

export interface ProductAddedToCartEvent extends BaseAnalyticsEvent, ProductAddedToCartValue {
  action: typeof ACTION_TYPES.PRODUCT_ADDED_TO_CART;
}

export interface ProductRemovedFromCartEvent extends BaseAnalyticsEvent, ProductRemovedFromCartValue {
  action: typeof ACTION_TYPES.PRODUCT_REMOVED_FROM_CART;
}

export interface ProductsSearchedEvent extends BaseAnalyticsEvent, ProductsSearchedValue {
  action: typeof ACTION_TYPES.PRODUCTS_SEARCHED;
}

export interface NavigatedToUrlEvent extends BaseAnalyticsEvent, NavigatedToUrlValue {
  action: typeof ACTION_TYPES.NAVIGATED_TO_URL;
}

export interface OrderCapturedEvent extends BaseAnalyticsEvent, NormalizedOrder {
  action: typeof ACTION_TYPES.ORDER_CAPTURED;
}

export interface ToolCalledEvent extends BaseAnalyticsEvent, ToolCalledValue {
  action: typeof ACTION_TYPES.TOOL_CALLED;
}

export type AnalyticsEventAction =
  | CartQuantityUpdatedEvent
  | CheckoutCancelledEvent
  | CheckoutInitiatedEvent
  | CheckoutOptionTappedEvent
  | FirstMessageTypedEvent
  | MessageReceivedEvent
  | MessageSentEvent
  | NavigatedToUrlEvent
  | OrderCapturedEvent
  | ProductAddedToCartEvent
  | ProductClickedEvent
  | ProductRemovedFromCartEvent
  | ProductsRecommendedEvent
  | ProductsSearchedEvent
  | PurchaseCompletedEvent
  | ToolCalledEvent
  | WidgetInputFocusedEvent
  | WidgetLoadedEvent
  | WidgetViewChangedEvent;

export interface Initiator {
  id: string;
  type: InitiatorType;
}

export const SYSTEM_INITIATOR: Initiator = {
  id: 'CHAT_SDK',
  type: INITIATOR_TYPES.SYSTEM,
};

export const UNKNOWN_CUSTOMER_INITIATOR: Initiator = {
  id: 'UNKNOWN_USER',
  type: INITIATOR_TYPES.CUSTOMER,
};

export function getCustomerInitiator(gladlyCustomerId: string | null): Initiator {
  if (gladlyCustomerId) {
    return { id: gladlyCustomerId, type: INITIATOR_TYPES.CUSTOMER };
  }
  return UNKNOWN_CUSTOMER_INITIATOR;
}

export const WIDGET_STATES = {
  DEFAULT: 'default',
  ACTIVE: 'active',
  MAXIMIZED: 'maximized',
  SIDEBAR: 'sidebar',
  BUBBLE: 'bubble',
} as const;

export type WidgetState = (typeof WIDGET_STATES)[keyof typeof WIDGET_STATES];

export type WidgetTransitionSource = 'API' | 'USER_INTERACTION';
